import { useEffect, useId, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@dental-staffing-platform/auth';
import { z } from 'zod';
import {
  reviewCaseOperation,
  reviewModerationInput,
  type GovernedReview,
  type ReviewCaseOperation,
  type ReviewModerationInput,
} from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
  Textarea,
} from '@dental-staffing-platform/ui';
import { reviewModerationApi as api } from '../lib/review-moderation-client.js';
import { AdminApiError } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

function formatRecordedAt(value: string): string {
  const utc = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
  const instant = new Date(utc);
  return Number.isFinite(instant.getTime())
    ? instant.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : 'Time unavailable';
}

const pendingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('decision'), id: z.string(), input: reviewModerationInput }),
  z.object({ kind: z.literal('operation'), id: z.string(), input: reviewCaseOperation }),
]);
type Pending = z.infer<typeof pendingSchema>;
function load(key: string): Pending | null {
  try {
    const parsed = pendingSchema.safeParse(JSON.parse(sessionStorage.getItem(key) ?? 'null'));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
function useSave(key: string, onSaved: () => void) {
  const active = useRef(true),
    inFlight = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const [pending, setPending] = useState(() => load(key));
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  function retain(value: Pending | null) {
    setPending(value);
    try {
      if (value) sessionStorage.setItem(key, JSON.stringify(value));
      else sessionStorage.removeItem(key);
    } catch {
      /* In-memory recovery remains available. */
    }
  }
  async function send(value: Pending) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    retain(value);
    try {
      if (value.kind === 'decision') await api.decide(value.id, value.input);
      else await api.operate(value.id, value.input);
      if (!active.current) return;
      retain(null);
      setNotice('Review update saved.');
      onSaved();
    } catch (cause) {
      if (!active.current) return;
      setError(
        cause instanceof Error
          ? cause.message
          : 'The update could not be confirmed. Retry the same request.'
      );
      if (cause instanceof AdminApiError && [400, 403, 404, 413, 422, 429].includes(cause.status))
        retain(null);
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  }
  return {
    pending,
    busy,
    error,
    notice,
    locked: busy || !!pending,
    send,
    discard: () => {
      retain(null);
      setError('');
      onSaved();
    },
  };
}
function Recovery({ save }: { save: ReturnType<typeof useSave> }) {
  const [ack, setAck] = useState(false);
  return (
    <>
      {save.notice && <p role="status">{save.notice}</p>}
      {save.error && <Card role="alert">{save.error}</Card>}
      {save.pending && (
        <Card>
          <Stack gap={12}>
            <h2>Check an unconfirmed review update</h2>
            <p>
              The earlier update may already be saved. Retry the exact request to confirm its
              outcome, or inspect the history before discarding it.
            </p>
            <p>
              {humanize(save.pending.input.action)}:{' '}
              {save.pending.kind === 'decision'
                ? save.pending.input.reason
                : save.pending.input.message}
            </p>
            <Button disabled={save.busy} onClick={() => void save.send(save.pending!)}>
              Retry same review update
            </Button>
            <label>
              <input
                type="checkbox"
                checked={ack}
                disabled={save.busy}
                onChange={(e) => setAck(e.target.checked)}
              />{' '}
              I checked the history and understand the earlier update may have succeeded.
            </label>
            <Button
              variant="secondary"
              disabled={save.busy || !ack}
              onClick={() => {
                setAck(false);
                save.discard();
              }}
            >
              Discard retained review update
            </Button>
          </Stack>
        </Card>
      )}
    </>
  );
}
function ReviewSummary({ review }: { review: GovernedReview }) {
  return (
    <Card>
      <Stack gap={12}>
        <Row style={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <h2>{review.locationName}</h2>
          <Badge tone={review.visibility === 'hidden' ? 'warning' : 'success'}>
            {review.visibility === 'hidden' ? 'Hidden from ratings' : 'Included in ratings'}
          </Badge>
        </Row>
        <p>
          {review.professionalName} · Feedback by {review.authorName}
        </p>
        <p>{review.stars === null ? 'Rating unavailable' : `${review.stars} of 5 stars`}</p>
        <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {review.body ?? 'No written feedback.'}
        </p>
        <p>Original feedback is retained for review. Visibility changes do not edit its words.</p>
        {review.decisionReason && <p>Current shared decision: {review.decisionReason}</p>}
        {review.accessNotice && <p className="dsp-muted">{review.accessNotice}</p>}
      </Stack>
    </Card>
  );
}
export function ReviewModeration() {
  const [params, setParams] = useSearchParams();
  const field = useId(),
    view = params.get('view') === 'reviews' ? 'reviews' : 'cases';
  const search = params.get('search') ?? '',
    visibility = params.get('visibility') ?? 'all';
  const status = params.get('status') ?? 'all',
    kind = params.get('kind') ?? 'all';
  const reviewId = params.get('reviewId') ?? '';
  const rawPage = Number(params.get('page') ?? '1'),
    page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const key = JSON.stringify({ view, search, visibility, status, kind, reviewId, page });
  const reviews = useAsync(
    async () => ({
      key,
      view:
        view === 'reviews'
          ? await api.reviews({
              page,
              pageSize: 12,
              search,
              visibility:
                visibility === 'hidden'
                  ? 'hidden'
                  : visibility === 'published'
                    ? 'published'
                    : 'all',
            })
          : null,
    }),
    [key]
  );
  const cases = useAsync(
    async () => ({
      key,
      view:
        view === 'cases'
          ? await api.cases({
              page,
              pageSize: 12,
              search,
              status:
                status === 'open' ||
                status === 'in_review' ||
                status === 'resolved' ||
                status === 'withdrawn'
                  ? status
                  : 'all',
              kind: kind === 'report' || kind === 'appeal' ? kind : 'all',
              ...(reviewId ? { reviewId } : {}),
            })
          : null,
    }),
    [key]
  );
  const state = view === 'reviews' ? reviews : cases;
  const reviewData = reviews.data?.key === key && !reviews.loading ? reviews.data.view : null;
  const caseData = cases.data?.key === key && !cases.loading ? cases.data.view : null;
  const data = view === 'reviews' ? reviewData : caseData;
  function filter(name: string, value: string) {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(name, value);
      else next.delete(name);
      next.delete('page');
      return next;
    });
  }
  useEffect(() => {
    if (data && page > Math.max(1, data.totalPages))
      setParams((current) => {
        const next = new URLSearchParams(current);
        next.set('page', String(Math.max(1, data.totalPages)));
        return next;
      });
  }, [data, page, setParams]);
  return (
    <Stack gap={20} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <PageHeader
        title="Review moderation"
        subtitle="Review feedback, reports and appeals with a clear record of each decision."
        actions={
          <Button
            onClick={() => {
              reviews.reload();
              cases.reload();
            }}
          >
            Refresh moderation queue
          </Button>
        }
      />
      <p className="dsp-muted">
        Reports do not automatically hide feedback. A visibility decision changes rating inclusion;
        resolving a report or appeal records its outcome. Neither action changes bookings, pay,
        strikes or account status.
      </p>
      <Row style={{ flexWrap: 'wrap' }}>
        <Button
          variant={view === 'cases' ? 'primary' : 'secondary'}
          aria-pressed={view === 'cases'}
          onClick={() => filter('view', 'cases')}
        >
          Reports & appeals
        </Button>
        <Button
          variant={view === 'reviews' ? 'primary' : 'secondary'}
          aria-pressed={view === 'reviews'}
          onClick={() => filter('view', 'reviews')}
        >
          All reviews
        </Button>
      </Row>
      {reviewId && (
        <p>
          Reports and appeals for the selected review.{' '}
          <Button variant="ghost" onClick={() => filter('reviewId', '')}>
            Show all review cases
          </Button>
        </p>
      )}
      <Card>
        <Row style={{ flexWrap: 'wrap', alignItems: 'end' }}>
          <Field label="Search moderation queue" htmlFor={`${field}-search`}>
            <Input
              id={`${field}-search`}
              type="search"
              value={search}
              maxLength={100}
              onChange={(e) => filter('search', e.target.value)}
            />
          </Field>
          {view === 'reviews' ? (
            <Field label="Review visibility" htmlFor={`${field}-visibility`}>
              <Select
                id={`${field}-visibility`}
                value={visibility}
                onChange={(e) => filter('visibility', e.target.value)}
              >
                <option value="all">All reviews</option>
                <option value="published">Included in ratings</option>
                <option value="hidden">Hidden from ratings</option>
              </Select>
            </Field>
          ) : (
            <>
              <Field label="Review case status" htmlFor={`${field}-status`}>
                <Select
                  id={`${field}-status`}
                  value={status}
                  onChange={(e) => filter('status', e.target.value)}
                >
                  {['all', 'open', 'in_review', 'resolved', 'withdrawn'].map((v) => (
                    <option key={v} value={v}>
                      {humanize(v)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Review case type" htmlFor={`${field}-kind`}>
                <Select
                  id={`${field}-kind`}
                  value={kind}
                  onChange={(e) => filter('kind', e.target.value)}
                >
                  {['all', 'report', 'appeal'].map((v) => (
                    <option key={v} value={v}>
                      {humanize(v)}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}
          <Button variant="secondary" onClick={() => setParams({ view })}>
            Clear moderation filters
          </Button>
        </Row>
      </Card>
      {state.loading ? (
        <SkeletonList count={3} />
      ) : state.error ? (
        <Card role="alert">
          <p>{state.error}</p>
          <Button onClick={state.reload}>Retry moderation queue</Button>
        </Card>
      ) : !data?.items.length ? (
        <EmptyState
          title="No moderation records match"
          description="Change the filters or refresh this queue."
        />
      ) : (
        <>
          {view === 'reviews'
            ? reviewData?.items.map((item) => (
                <Card key={item.id}>
                  <Stack gap={12}>
                    <h2>{item.locationName}</h2>
                    <p>
                      {item.professionalName} · {item.authorName}
                    </p>
                    <Badge tone={item.visibility === 'hidden' ? 'warning' : 'success'}>
                      {item.visibility === 'hidden' ? 'Hidden from ratings' : 'Included in ratings'}
                    </Badge>
                    <p>{item.stars} of 5 stars</p>
                    <Link to={`/review-moderation/reviews/${encodeURIComponent(item.id)}`}>
                      Inspect review
                    </Link>
                  </Stack>
                </Card>
              ))
            : caseData?.items.map((item) => (
                <Card key={item.id}>
                  <Stack gap={12}>
                    <Row style={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
                      <h2>
                        {humanize(item.kind)} at {item.locationName}
                      </h2>
                      <Badge>{humanize(item.status)}</Badge>
                    </Row>
                    <p>
                      {humanize(item.concern)} · Updated {formatRecordedAt(item.updatedAt)}
                    </p>
                    <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {item.message}
                    </p>
                    <Link to={`/review-moderation/cases/${encodeURIComponent(item.id)}`}>
                      Inspect review case
                    </Link>
                  </Stack>
                </Card>
              ))}
          <Pagination
            {...data}
            itemLabel="moderation records"
            onPageChange={(value) => {
              setParams((current) => {
                const next = new URLSearchParams(current);
                next.set('page', String(value));
                return next;
              });
            }}
          />
        </>
      )}
    </Stack>
  );
}

export function ModeratedReviewDetail() {
  const { reviewId } = useParams(),
    { user } = useAuth();
  if (!reviewId) return <p role="alert">Choose a review from the moderation queue.</p>;
  const storageKey = `review-decision:${user?.organizationId ?? ''}:${user?.id ?? ''}:${reviewId}`;
  return <Decision key={storageKey} reviewId={reviewId} storageKey={storageKey} />;
}
function Decision({ reviewId, storageKey }: { reviewId: string; storageKey: string }) {
  const field = useId(),
    [page, setPage] = useState(1),
    [changed, setChanged] = useState(0);
  const key = JSON.stringify({ reviewId, page, changed });
  const state = useAsync(async () => ({ key, view: await api.review(reviewId, page) }), [key]);
  const data = state.data?.key === key && !state.loading && !state.error ? state.data.view : null;
  const [action, setAction] = useState<ReviewModerationInput['action']>('keep'),
    [reason, setReason] = useState(''),
    [ack, setAck] = useState(false);
  const save = useSave(storageKey, () => {
    setAck(false);
    setReason('');
    setAction('keep');
    setChanged((v) => v + 1);
  });
  useEffect(() => setAck(false), [data?.review.moderationRevision]);
  const decision = data?.review.allowedDecisions.includes(action)
    ? action
    : data?.review.allowedDecisions[0];
  return (
    <Stack gap={20} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Link to="/review-moderation?view=reviews">Back to all reviews</Link>
      <PageHeader
        title="Review decision"
        subtitle="Keep an auditable reason and preserve the original feedback."
        actions={
          <Button disabled={save.busy} onClick={state.reload}>
            Refresh review history
          </Button>
        }
      />
      <Recovery save={save} />
      {state.loading ? (
        <SkeletonList count={2} />
      ) : state.error ? (
        <Card role="alert">
          <p>{state.error}</p>
          <Button onClick={state.reload}>Retry review detail</Button>
        </Card>
      ) : (
        data && (
          <>
            <ReviewSummary review={data.review} />
            <Link to={`/review-moderation?view=cases&reviewId=${encodeURIComponent(reviewId)}`}>
              Reports and appeals for this review
            </Link>
            {data.review.canModerate ? (
              <Card>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!ack || save.locked || !decision) return;
                    void save.send({
                      kind: 'decision',
                      id: reviewId,
                      input: {
                        action: decision,
                        reason,
                        expectedRevision: data.review.moderationRevision,
                        clientRequestId: crypto.randomUUID(),
                      },
                    });
                  }}
                >
                  <Stack gap={12}>
                    <h2>Record a visibility decision</h2>
                    <Field label="Review decision" htmlFor={`${field}-decision`}>
                      <Select
                        id={`${field}-decision`}
                        value={decision ?? ''}
                        disabled={save.locked}
                        onChange={(e) => {
                          setAction(e.target.value as ReviewModerationInput['action']);
                          setAck(false);
                        }}
                      >
                        {data.review.allowedDecisions.map((value) => (
                          <option key={value} value={value}>
                            {value === 'keep'
                              ? 'Keep current visibility'
                              : value === 'hide'
                                ? 'Hide review and exclude its rating'
                                : 'Restore review and include its rating'}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Shared decision reason" htmlFor={`${field}-reason`}>
                      <Textarea
                        id={`${field}-reason`}
                        value={reason}
                        disabled={save.locked}
                        minLength={10}
                        maxLength={2000}
                        onChange={(e) => {
                          setReason(e.target.value);
                          setAck(false);
                        }}
                      />
                    </Field>
                    <p>
                      This reason is visible to eligible review participants. Do not include private
                      operator notes or patient information. The original review remains preserved.
                    </p>
                    <label>
                      <input
                        type="checkbox"
                        checked={ack}
                        disabled={save.locked}
                        onChange={(e) => setAck(e.target.checked)}
                      />{' '}
                      I reviewed the current visibility and rating effect of this decision.
                    </label>
                    <Button
                      type="submit"
                      disabled={save.locked || !ack || reason.trim().length < 10}
                    >
                      Confirm review decision
                    </Button>
                  </Stack>
                </form>
              </Card>
            ) : (
              <Card>
                <p>Another operator must make this decision. You are involved in this review.</p>
              </Card>
            )}
            <Card>
              <Stack gap={12}>
                <h2>Visibility decision history</h2>
                {!data.history.items.length ? (
                  <p>
                    No moderation decisions are recorded. Earlier reviews keep their original rating
                    inclusion without invented history.
                  </p>
                ) : (
                  data.history.items.map((event) => (
                    <div key={event.id}>
                      <h3>
                        {humanize(event.action)} · Revision {event.revision}
                      </h3>
                      <p>
                        {event.actorName} · {formatRecordedAt(event.createdAt)}
                      </p>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{event.reason}</p>
                    </div>
                  ))
                )}
                <Pagination
                  {...data.history}
                  itemLabel="visibility decisions"
                  onPageChange={setPage}
                />
              </Stack>
            </Card>
          </>
        )
      )}
    </Stack>
  );
}

export function ModerationCaseDetail() {
  const { caseId } = useParams(),
    { user } = useAuth();
  if (!caseId) return <p role="alert">Choose a report or appeal from the queue.</p>;
  const storageKey = `review-case-operator:${user?.organizationId ?? ''}:${user?.id ?? ''}:${caseId}`;
  return <Case key={storageKey} caseId={caseId} storageKey={storageKey} />;
}
function Case({ caseId, storageKey }: { caseId: string; storageKey: string }) {
  const field = useId(),
    [page, setPage] = useState(1),
    [changed, setChanged] = useState(0);
  const key = JSON.stringify({ caseId, page, changed });
  const state = useAsync(async () => ({ key, view: await api.case(caseId, page) }), [key]);
  const data = state.data?.key === key && !state.loading && !state.error ? state.data.view : null;
  const [action, setAction] = useState<ReviewCaseOperation['action']>('start_review'),
    [message, setMessage] = useState(''),
    [ack, setAck] = useState(false);
  const save = useSave(storageKey, () => {
    setAck(false);
    setMessage('');
    setAction('start_review');
    setChanged((v) => v + 1);
  });
  useEffect(() => setAck(false), [data?.case.revision]);
  const operation = data?.case.allowedOperations.includes(action)
    ? action
    : data?.case.allowedOperations[0];
  return (
    <Stack gap={20} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Link to="/review-moderation">Back to reports and appeals</Link>
      <PageHeader
        title="Review report or appeal"
        subtitle="Keep case evidence separate from the shared visibility decision."
        actions={
          <Button disabled={save.busy} onClick={state.reload}>
            Refresh review case
          </Button>
        }
      />
      <Recovery save={save} />
      {state.loading ? (
        <SkeletonList count={2} />
      ) : state.error ? (
        <Card role="alert">
          <p>{state.error}</p>
          <Button onClick={state.reload}>Retry review case</Button>
        </Card>
      ) : (
        data && (
          <>
            <Card>
              <Stack gap={12}>
                <h2>
                  {humanize(data.case.kind)} at {data.case.locationName}
                </h2>
                <Badge>{humanize(data.case.status)}</Badge>
                <p>{humanize(data.case.concern)}</p>
                <h3>Private claimant evidence</h3>
                <p style={{ whiteSpace: 'pre-wrap' }}>{data.case.message}</p>
                <p>
                  This case and its evidence are visible only to its claimant and review operators.
                </p>
                {data.case.resolution && <p>Resolution: {data.case.resolution}</p>}
                {data.case.accessNotice && <p>{data.case.accessNotice}</p>}
              </Stack>
            </Card>
            <ReviewSummary review={data.review} />
            <Link to={`/review-moderation/reviews/${encodeURIComponent(data.review.id)}`}>
              Inspect review and record a visibility decision
            </Link>
            {data.case.canOperate ? (
              <Card>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!ack || save.locked || !operation) return;
                    void save.send({
                      kind: 'operation',
                      id: caseId,
                      input: {
                        action: operation,
                        message,
                        expectedRevision: data.case.revision,
                        clientRequestId: crypto.randomUUID(),
                      },
                    });
                  }}
                >
                  <Stack gap={12}>
                    <h2>Update this report or appeal</h2>
                    <Field label="Review case action" htmlFor={`${field}-action`}>
                      <Select
                        id={`${field}-action`}
                        value={operation ?? ''}
                        disabled={save.locked}
                        onChange={(e) => {
                          setAction(e.target.value as ReviewCaseOperation['action']);
                          setAck(false);
                        }}
                      >
                        {data.case.allowedOperations.map((value) => (
                          <option key={value} value={value}>
                            {value === 'start_review'
                              ? 'Start review'
                              : value === 'reply'
                                ? 'Reply to claimant'
                                : value === 'note'
                                  ? 'Private operator note'
                                  : 'Resolve case'}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field
                      label={
                        operation === 'note' ? 'Private operator note' : 'Message for claimant'
                      }
                      htmlFor={`${field}-message`}
                    >
                      <Textarea
                        id={`${field}-message`}
                        value={message}
                        disabled={save.locked}
                        maxLength={3000}
                        onChange={(e) => {
                          setMessage(e.target.value);
                          setAck(false);
                        }}
                      />
                    </Field>
                    <p>
                      {operation === 'note'
                        ? 'This note stays in operator history and is not shown to the claimant or the other participant.'
                        : 'This update is visible to the claimant in their private case. Resolving a case does not change review visibility; make a separate visibility decision when needed.'}
                    </p>
                    <label>
                      <input
                        type="checkbox"
                        checked={ack}
                        disabled={save.locked}
                        onChange={(e) => setAck(e.target.checked)}
                      />{' '}
                      I checked the recipient and effect of this case update.
                    </label>
                    <Button type="submit" disabled={save.locked || !ack || !message.trim()}>
                      Confirm review case update
                    </Button>
                  </Stack>
                </form>
              </Card>
            ) : (
              <p>Another operator must act on this case. You are involved in the review.</p>
            )}
            <Card>
              <Stack gap={12}>
                <h2>Report and appeal history</h2>
                {data.events.items.map((event) => (
                  <section key={event.id}>
                    <Row style={{ flexWrap: 'wrap' }}>
                      <h3>
                        {humanize(event.kind)} · Revision {event.revision}
                      </h3>
                      <Badge tone={event.private ? 'warning' : 'info'}>
                        {event.private ? 'Private operator note' : 'Visible to claimant'}
                      </Badge>
                    </Row>
                    <p>
                      {event.actorName} · {formatRecordedAt(event.createdAt)}
                    </p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{event.message}</p>
                  </section>
                ))}
                <Pagination {...data.events} itemLabel="case events" onPageChange={setPage} />
              </Stack>
            </Card>
          </>
        )
      )}
    </Stack>
  );
}
